import {FC, useState, useEffect} from 'react'
import {KTIcon} from '../../../../_metronic/helpers'
import {ClassifiedSubCategory, ClassifiedCategory} from '../core/_models'
import {createClassifiedSubCategory, updateClassifiedSubCategory} from '../core/_requests'
import {toast} from 'react-toastify'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

const stripHtml = (value?: string | null) =>
  value
    ? value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : ''

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{list: 'ordered'}, {list: 'bullet'}],
    ['link'],
    ['clean'],
  ],
}

const quillFormats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link']

type Props = {
  subCategory: ClassifiedSubCategory | null
  categories: ClassifiedCategory[]
  onClose: () => void
  onSave: () => void
}

const SubCategoryModal: FC<Props> = ({subCategory, categories, onClose, onSave}) => {
  const [formData, setFormData] = useState<ClassifiedSubCategory>({
    name: '',
    category: '',
    description: '',
    status: 'Active',
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (subCategory) {
      setFormData({
        ...subCategory,
        category:
          typeof subCategory.category === 'object' ? subCategory.category.id : subCategory.category,
      } as ClassifiedSubCategory)
    }
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [subCategory])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      if (subCategory?.id) {
        await updateClassifiedSubCategory(formData)
        toast.success('SubCategory updated successfully')
      } else {
        await createClassifiedSubCategory(formData)
        toast.success('SubCategory created successfully')
      }
      onSave()
    } catch (error) {
      toast.error('Failed to save subcategory')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <div
        className='modal fade show d-block'
        id='kt_modal_add_subcategory'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder'>{subCategory?.id ? 'Edit' : 'Add'} SubCategory</h2>
              <div
                className='btn btn-icon btn-sm btn-active-icon-primary'
                onClick={onClose}
                style={{cursor: 'pointer'}}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>

            <div className='modal-body scroll-y mx-5 mx-xl-15 my-7'>
              <form onSubmit={handleSubmit}>
                {/* Category */}
                <div className='fv-row mb-7'>
                  <label className='required fw-bold fs-6 mb-2'>Category</label>
                  <select
                    className='form-select form-select-solid'
                    value={formData.category as string}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    <option value=''>Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id || ''}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div className='fv-row mb-7'>
                  <label className='required fw-bold fs-6 mb-2'>Name</label>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    placeholder='SubCategory name'
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>

                {/* Description */}
                <div className='fv-row mb-7'>
                  <label className='fw-bold fs-6 mb-2'>Description</label>
                  <ReactQuill
                    theme='snow'
                    value={formData.description || ''}
                    onChange={(value) => {
                      const sanitizedValue = stripHtml(value) ? value : ''
                      setFormData((prev) => ({...prev, description: sanitizedValue}))
                    }}
                    modules={quillModules}
                    formats={quillFormats}
                    readOnly={submitting}
                    placeholder='Subcategory description'
                  />
                </div>

                {/* Status */}
                <div className='fv-row mb-7'>
                  <label className='fw-bold fs-6 mb-2'>Status</label>
                  <select
                    className='form-select form-select-solid'
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({...formData, status: e.target.value as 'Active' | 'Inactive'})
                    }
                  >
                    <option value='Active'>Active</option>
                    <option value='Inactive'>Inactive</option>
                  </select>
                </div>

                {/* Actions */}
                <div className='text-center pt-15'>
                  <button
                    type='button'
                    onClick={onClose}
                    className='btn btn-light me-3'
                    disabled={submitting}
                  >
                    Cancel
                  </button>

                  <button type='submit' className='btn btn-primary' disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export {SubCategoryModal}
